import { useState, useRef, useEffect, ReactNode } from "react";
import { useClickOutside } from "hooks/useClickOutside";
import classNames from "classnames";
import { Paragraph, Header } from "../Typography";
import { DownCarrot } from "../Icon";

import "./index.scss";

export interface Item {
    id: any;
    displayValue: string;
    // displayView?: ReactNode;
    element?: ReactNode;
    itemId?: string;
    disable?: boolean;
}
interface DropdownProps {
    items: Item[];
    onSelectItem: (item: Item) => void;
    defaultItem?: Item;
    placeholder?: string;
    classname?: string;
    style?: React.CSSProperties;
    selectId?: string;
    selectorDataTestId?: string;
    hideTitle?: boolean;
    humanReadable?: string;
    required?: boolean;
}

function CarrotIcon({ open }: { open: boolean }) {
    return (
        <div className={`dropdown-carrot-icon ${open && "open"}`}>
            <DownCarrot />
        </div>
    );
}

function Dropdown({
    items,
    defaultItem,
    onSelectItem,
    placeholder,
    classname,
    style,
    selectId,
    selectorDataTestId,
    hideTitle = true,
    humanReadable = "",
    required,
}: DropdownProps) {
    const [menuIsOpen, setMenuIsOpen] = useState(false);
    const ref = useRef(null);
    useClickOutside(ref, () => setMenuIsOpen(false));

    const { initialDisplayValue, setInitialDisplayValue } = useInitialDisplayValue(defaultItem, placeholder);

    return (
        <div className="dropdown-container">
            {!hideTitle && (
                <Header size="small">
                    {humanReadable}
                    {required && <span style={{ color: "var(--audette-content-negative)" }}>*</span>}
                </Header>
            )}
            <div className={classNames("single-select-dropdown", classname && classname)} ref={ref} style={style}>
                <button
                    type="button"
                    id={selectId}
                    className="selector"
                    onClick={() => {
                        setMenuIsOpen((open) => !open);
                    }}
                    data-testid={selectorDataTestId}
                >
                    {typeof initialDisplayValue === "string" ? (
                        <>
                            <Header size="small"> {initialDisplayValue} </Header>
                            <CarrotIcon open={menuIsOpen} />{" "}
                        </>
                    ) : (
                        <>
                            {initialDisplayValue}
                            <CarrotIcon open={menuIsOpen} />{" "}
                        </>
                    )}
                </button>
                {menuIsOpen && (
                    <div className="menu">
                        {items.map((item) => (
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px", // spacing between icon and name
                                }}
                                key={item.id}
                            >
                                <ItemRow
                                    item={item}
                                    onClick={() => {
                                        setInitialDisplayValue(item.element || item.displayValue);
                                        onSelectItem(item);
                                        setMenuIsOpen(false);
                                    }}
                                />
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function ItemRow({ item, onClick }: { item: Item; onClick: () => void }) {
    if (item.disable) {
        return item.element ? item.element : <Paragraph>{item.displayValue}</Paragraph>;
    }
    return (
        <button type="button" id={`dropdown-item-${item.id}`} className="menu-item" onClick={onClick}>
            {item.element ? item.element : <Paragraph>{item.displayValue}</Paragraph>}
        </button>
    );
}

const useInitialDisplayValue = (defaultItem?: Item, placeholder?: string) => {
    const defaultItemDisplayValue =
        defaultItem?.element || defaultItem?.displayValue || placeholder || "Select an option";

    const [initialDisplayValue, setInitialDisplayValue] = useState(defaultItemDisplayValue);

    useEffect(() => {
        setInitialDisplayValue(defaultItemDisplayValue);
    }, [defaultItemDisplayValue]);

    return { initialDisplayValue, setInitialDisplayValue };
};

export default Dropdown;
